import { PAT_EXEMPT_TOUR, PAT_TYPE_OPTIONS } from "../../utils/pat";
import Select from "@/components/Select";
import TextField from "@/components/TextField";
import DateSelect from "@/components/DateSelect";
import { DateValueType } from "react-tailwindcss-datepicker";
import FileUpload from "@/components/FileUpload";
import Banner from "@/components/Banner/Banner";
import { useEffect, useState } from "react";

export interface PATSubmission {
  id: string;
  patType: string;
  date: string;
  name?: string | null;
  roundOneScore?: number | null;
  roundTwoScore?: number | null;
  targetScore?: number | null;
  note?: string | null;
  tour?: string | null;
  handicap?: number | null;
  document?: {
    id?: string;
    url?: string;
    name?: string;
  } | null;
}

const PATStatusNotificationNote = () => {
  return (
    <div className="space-y-4">
      <p className="text-body-medium">
        <span className="font-bold">Note:</span> If the result of this update also changes the overall PAT status for
        this person, they will be sent an email informing them of the new status.
      </p>
    </div>
  );
};

const PATActivityTypeSelect = ({
  value,
  onChange,
  disabled = false,
  error,
  errorText,
  clearFieldError
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  clearFieldError?: (fieldName: string) => void;
}) => {
  return (
    <Select
      label="Type"
      required
      options={PAT_TYPE_OPTIONS}
      value={value}
      onSelect={(val) => {
        onChange(val);
        clearFieldError?.("type");
      }}
      error={error}
      errorText={errorText}
      className="w-full"
      disabled={disabled}
    />
  );
};

const HandicapTextField = ({
  value,
  onChange,
  error,
  errorText,
  clearFieldError
}: {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  errorText?: string;
  clearFieldError?: (fieldName: string) => void;
}) => {
  const [localError, setLocalError] = useState<string>("");

  useEffect(() => {
    if (parseFloat(value) > 10) {
      setLocalError("Must be 10 or less to be used as qualifying score");
    } else {
      setLocalError("");
    }
  }, [value]);

  const hasError = error || !!localError;
  const displayErrorText = errorText || localError;

  return (
    <TextField
      label="Handicap"
      required
      type="text"
      value={value}
      onChangeText={(val) => {
        onChange(val);
        clearFieldError?.("handicapScore");
      }}
      error={hasError}
      errorText={displayErrorText}
      {...(!hasError && { supportingText: "Must be 10 or less to be used as qualifying score" })}
      className="w-full"
    />
  );
};

const TourExemptionSelect = ({
  value,
  onChange,
  error,
  errorText,
  clearFieldError
}: {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  errorText?: string;
  clearFieldError?: (fieldName: string) => void;
}) => {
  return (
    <Select
      label="Reason"
      required
      options={PAT_EXEMPT_TOUR}
      value={value}
      onSelect={(val) => {
        onChange(val);
        clearFieldError?.("reason");
      }}
      error={error}
      errorText={errorText}
      className="w-full"
    />
  );
};

const AlternateEventTextFields = ({
  eventName,
  setEventName,
  eventDate,
  setEventDate,
  first18HoleScore,
  setFirst18HoleScore,
  second18HoleScore,
  setSecond18HoleScore,
  targetScore,
  setTargetScore,
  fieldErrors,
  clearFieldError
}: {
  eventName: string;
  setEventName: (value: string) => void;
  eventDate: DateValueType | string;
  setEventDate: (date: DateValueType | string) => void;
  first18HoleScore: string;
  setFirst18HoleScore: (value: string) => void;
  second18HoleScore: string;
  setSecond18HoleScore: (value: string) => void;
  targetScore: string;
  setTargetScore: (value: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError?: (fieldName: string) => void;
}) => {
  return (
    <>
      <TextField
        label="Event Name"
        required
        type="text"
        value={eventName}
        onChangeText={(val) => {
          setEventName(val);
          clearFieldError?.("eventName");
        }}
        error={!!fieldErrors?.eventName}
        errorText={fieldErrors?.eventName}
        className="w-full"
      />
      <DateSelect
        label="Event Date *"
        value={eventDate}
        onChange={(val) => {
          setEventDate(val);
          clearFieldError?.("eventDate");
        }}
        useSingleDate
        maxDate={new Date()}
        useModal={true}
        error={!!fieldErrors?.eventDate}
        errorMessage={fieldErrors?.eventDate}
        className="w-full"
      />
      <div className="flex gap-4">
        <TextField
          label="First 18 Hole Score"
          required
          type="number"
          value={first18HoleScore}
          onChangeText={(val) => {
            setFirst18HoleScore(val);
            clearFieldError?.("first18HoleScore");
          }}
          error={!!fieldErrors?.first18HoleScore}
          errorText={fieldErrors?.first18HoleScore}
          className="flex-1"
        />
        <TextField
          label="Second 18 Hole Score"
          required
          type="number"
          value={second18HoleScore}
          onChangeText={(val) => {
            setSecond18HoleScore(val);
            clearFieldError?.("second18HoleScore");
          }}
          error={!!fieldErrors?.second18HoleScore}
          errorText={fieldErrors?.second18HoleScore}
          className="flex-1"
        />
      </div>
      <TextField
        label="Target Score"
        required
        type="number"
        value={targetScore}
        onChangeText={(val) => {
          setTargetScore(val);
          clearFieldError?.("targetScore");
        }}
        error={!!fieldErrors?.targetScore}
        errorText={fieldErrors?.targetScore}
        className="w-full"
      />
    </>
  );
};

const NoteTextField = ({
  value,
  onChange,
  eventType,
  error,
  errorText,
  clearFieldError
}: {
  value: string;
  onChange: (value: string) => void;
  eventType: string;
  error?: boolean;
  errorText?: string;
  clearFieldError?: (fieldName: string) => void;
}) => {
  return (
    <TextField
      label="Details"
      required={eventType === "alternate_event" || eventType === "combined_events"}
      type="textarea"
      rows={4}
      value={value}
      onChangeText={(val) => {
        onChange(val);
        clearFieldError?.("notes");
      }}
      error={error}
      errorText={errorText}
      className="w-full"
    />
  );
};

const PATFileUpload = ({
  selectedFile,
  setSelectedFile,
  setFileUploadErrorMsg,
  fileUploadErrorMsg,
  existingDocument
}: {
  selectedFile: File | null;
  setSelectedFile: (file: File) => void;
  setFileUploadErrorMsg: (error: string) => void;
  fileUploadErrorMsg: string;
  existingDocument?: { id?: string; url?: string; name?: string } | null;
}) => {
  const allowedExtensions = ".pdf, .jpg, .png";
  const handleUpload = (file: File) => {
    setSelectedFile(file);
    setFileUploadErrorMsg("");
  };

  return (
    <div className="mt-6">
      <FileUpload
        overline="PAT Documentation"
        accept={allowedExtensions}
        subtitle={`Allowed file extensions: ${allowedExtensions.replace(".", "")}`}
        handleUpload={handleUpload}
        errorMsg={fileUploadErrorMsg}
        onError={(error) => setFileUploadErrorMsg(error)}
      />
      {existingDocument?.url && !selectedFile && (
        <p className="text-body-small text-sys-brand-on-surface-variant mt-2">
          Current document:{" "}
          <a
            href={existingDocument?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sys-brand-primary underline"
          >
            {existingDocument?.name || "View document"}
          </a>
        </p>
      )}
    </div>
  );
};

export interface PATModalContentProps {
  operation: "add" | "edit";
  submission?: PATSubmission;
  apiError: string;
  fieldErrors: Record<string, string>;
  clearFieldError: (fieldName: string) => void;
  patType: string;
  setPatType: (value: string) => void;
  handicapScore: string;
  setHandicapScore: (value: string) => void;
  reason: string;
  setReason: (value: string) => void;
  eventName: string;
  setEventName: (value: string) => void;
  eventDate: DateValueType | string;
  setEventDate: (date: DateValueType | string) => void;
  first18HoleScore: string;
  setFirst18HoleScore: (value: string) => void;
  second18HoleScore: string;
  setSecond18HoleScore: (value: string) => void;
  targetScore: string;
  setTargetScore: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  selectedFile: File | null;
  setSelectedFile: (file: File) => void;
  fileUploadErrorMsg: string;
  setFileUploadErrorMsg: (error: string) => void;
}

const PATModalContent = ({
  operation,
  submission,
  apiError,
  fieldErrors,
  clearFieldError,
  patType,
  setPatType,
  handicapScore,
  setHandicapScore,
  reason,
  setReason,
  eventName,
  setEventName,
  eventDate,
  setEventDate,
  first18HoleScore,
  setFirst18HoleScore,
  second18HoleScore,
  setSecond18HoleScore,
  targetScore,
  setTargetScore,
  notes,
  setNotes,
  selectedFile,
  setSelectedFile,
  fileUploadErrorMsg,
  setFileUploadErrorMsg
}: PATModalContentProps) => {
  const hasRequiredFieldErrors = Object.values(fieldErrors).some(
    (error) => error.includes("is required") || error === "Please select a valid PAT type"
  );

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const bannerMessage = hasRequiredFieldErrors ? "Required information missing" : "Invalid information, please review";

  return (
    <>
      <PATStatusNotificationNote />
      <div className="space-y-6 mt-6 mb-4">
        {apiError && <Banner variant="error" description={apiError} inline />}
        {hasFieldErrors && <Banner variant="error" description={bannerMessage} inline />}
        <PATActivityTypeSelect
          value={patType}
          onChange={setPatType}
          disabled={operation === "edit"}
          error={!!fieldErrors.type}
          errorText={fieldErrors.type}
          clearFieldError={clearFieldError}
        />
        {patType === "handicap_submission" && (
          <HandicapTextField
            value={handicapScore}
            onChange={setHandicapScore}
            error={!!fieldErrors.handicapScore}
            errorText={fieldErrors.handicapScore}
            clearFieldError={clearFieldError}
          />
        )}
        {patType === "tour_exemption" && (
          <TourExemptionSelect
            value={reason}
            onChange={setReason}
            error={!!fieldErrors.reason}
            errorText={fieldErrors.reason}
            clearFieldError={clearFieldError}
          />
        )}
        {(patType === "alternate_event" || patType === "combined_events") && (
          <AlternateEventTextFields
            eventName={eventName}
            setEventName={setEventName}
            eventDate={eventDate}
            setEventDate={setEventDate}
            first18HoleScore={first18HoleScore}
            setFirst18HoleScore={setFirst18HoleScore}
            second18HoleScore={second18HoleScore}
            setSecond18HoleScore={setSecond18HoleScore}
            targetScore={targetScore}
            setTargetScore={setTargetScore}
            fieldErrors={fieldErrors}
            clearFieldError={clearFieldError}
          />
        )}
        <NoteTextField
          value={notes}
          onChange={setNotes}
          eventType={patType}
          error={!!fieldErrors.notes}
          errorText={fieldErrors.notes}
          clearFieldError={clearFieldError}
        />
        <PATFileUpload
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          setFileUploadErrorMsg={setFileUploadErrorMsg}
          fileUploadErrorMsg={fileUploadErrorMsg}
          existingDocument={submission?.document}
        />
      </div>
    </>
  );
};

export default PATModalContent;
